/**
 * Talks to /auth/* endpoints. Mirrors the iOS AuthClient — same wire
 * format (snake_case backend payloads), translated to camelCase here.
 */

import { backendUrl } from "@/shared/config";
import type { StoredTokens } from "@/features/auth/lib/tokenStorage";

/** Backend's TokenResponse, with snake_case keys. */
interface RawTokenResponse {
  access_token: string;
  refresh_token: string;
  access_token_expires_at: string;
  refresh_token_expires_at: string;
  user_id: string;
}

function toStoredTokens(raw: RawTokenResponse): StoredTokens {
  return {
    accessToken: raw.access_token,
    refreshToken: raw.refresh_token,
    accessTokenExpiresAt: raw.access_token_expires_at,
    refreshTokenExpiresAt: raw.refresh_token_expires_at,
    userId: raw.user_id,
  };
}

/**
 * Thrown by signIn/refreshSession when /auth/* returns a non-2xx.
 * `status` is the HTTP status (undefined on network failures).
 */
export class AuthError extends Error {
  readonly status?: number;
  constructor(message: string, status?: number) {
    super(message);
    this.name = "AuthError";
    this.status = status;
  }
}

/**
 * POST /auth/sign-in with a provider credential (Apple identity token
 * or Google ID token). On success the backend creates the user if
 * they're new and returns a fresh access + refresh token pair.
 */
export async function signIn(params: {
  provider: "apple" | "google";
  credential: string;
  deviceLabel?: string;
}): Promise<StoredTokens> {
  const res = await fetch(backendUrl("/auth/sign-in"), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      provider: params.provider,
      credential: params.credential,
      device_label: params.deviceLabel ?? defaultDeviceLabel(),
    }),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({ detail: res.statusText }));
    throw new AuthError(body.detail ?? "Sign-in failed", res.status);
  }
  return toStoredTokens(await res.json());
}

/**
 * Exchange a refresh token for a fresh pair (rotation — the old
 * refresh token is invalidated server-side).
 */
export async function refreshSession(
  refreshToken: string,
): Promise<StoredTokens> {
  const res = await fetch(backendUrl("/auth/refresh"), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ refresh_token: refreshToken }),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({ detail: res.statusText }));
    throw new AuthError(body.detail ?? "Refresh failed", res.status);
  }
  return toStoredTokens(await res.json());
}

export async function signOut(refreshToken: string): Promise<void> {
  // Best-effort; revoking the refresh token server-side. If the server
  // doesn't accept it, we still wipe local state at the call site.
  await fetch(backendUrl("/auth/sign-out"), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ refresh_token: refreshToken }),
  }).catch(() => {
    // Network error during sign-out is non-fatal — local clear is enough.
  });
}

function defaultDeviceLabel(): string {
  const platform = navigator.platform || "Web";
  const ua = navigator.userAgent;
  const browser = /Chrome\/[\d.]+/.exec(ua)?.[0]
    ?? /Safari\/[\d.]+/.exec(ua)?.[0]
    ?? /Firefox\/[\d.]+/.exec(ua)?.[0]
    ?? "Browser";
  return `${platform} · ${browser}`;
}
